import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import MobileHeader from "../components/MobileHeader";
import {
  User,
  Wallet,
  Smartphone,
  Wifi,
  Zap,
  Tv,
  Headset,
  MessageSquare,
  Globe,
  Users,
  Gift,
  Phone,
  ShieldCheck,
  Eye,
  EyeOff,
} from "lucide-react";
import axios from "axios";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import config from "../config";

export default function DashboardPage() {
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [profile, setProfile] = useState(null);
  const [showBalance, setShowBalance] = useState(false);
  const navigate = useNavigate();

  // 🔑 Fetch user profile from backend

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        console.error("No token found");
        navigate("/login");
        return;
      }

      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);

        // Handle specific error cases
        if (err.response?.status === 401) {
          console.log("Token invalid or expired");
          localStorage.removeItem("authToken");
          navigate("/login");
        } else if (err.response?.status === 403) {
          console.log("Access forbidden");
        } else {
          console.log("Network or server error");
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  if (!profile) {
    return <p className="text-center mt-10">Loading profile...</p>;
  }

  const services = [
    {
      title: "SMS Verification",
      desc: "Receive OTPs & verification codes instantly.",
      icon: <MessageSquare className="w-8 h-8 text-[#007AFF]" />,
      status: "Available",
      link: "/dashboard/BuySmsRoutePage",
    },
    {
      title: "Airtime & Data",
      desc: "Top up airtime and data instantly.",
      icon: <Smartphone className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },
    {
      title: "Electricity Bills",
      desc: "Pay PHCN, EEDC, EKEDC & more.",
      icon: <Zap className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },
    {
      title: "Cable TV",
      desc: "Renew DStv, GOtv, StarTimes, etc.",
      icon: <Tv className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },
    {
      title: "Support Ticket",
      desc: "Get help from our dedicated support team.",
      icon: <Headset className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },

    {
      title: "Social Boost",
      desc: "Promote and grow your social media pages.",
      icon: <Users className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },
    {
      title: "Refer & Earn",
      desc: "Invite friends and earn rewards instantly.",
      icon: <Gift className="w-8 h-8 text-[#007AFF]" />,
      status: "Coming Soon",
    },
  ];

  return (
    <DashboardLayout>
      <ProtectDashboardRoute />
      {/* Top Section */}
      <MobileHeader />
      <section
        className="hidden md:flex bg-[#007AFF] text-white rounded-2xl p-6 md:p-8 
             flex flex-col md:flex-row justify-between items-center 
             gap-6 md:gap-0 mb-10 shadow-lg mt-14 md:mt-0"
      >
        {/* Left: User Info */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left gap-3 w-full md:w-auto">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-white text-[#007AFF] rounded-full flex items-center justify-center shadow">
            <User className="w-8 h-8 md:w-10 md:h-10" />
          </div>

          <div>
            <h2 className="text-lg md:text-2xl font-bold">Welcome back,</h2>
            <p className="text-sm md:text-lg font-medium">
              {profile.firstName} {profile.lastName}
            </p>
          </div>
        </div>

        {/* Right: Wallet Balance */}
        <div className="flex flex-col items-center md:items-end gap-3 w-full md:w-auto mt-4 md:mt-0">
          <div className="flex items-center gap-3">
            <p className="text-xl md:text-3xl font-bold">
              {showBalance
                ? `₦${(
                    Math.ceil((profile.wallet?.balance || 0) * 100) / 100
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : "••••••"}
            </p>

            <button
              onClick={() => setShowBalance(!showBalance)}
              className="text-white/80 hover:text-white transition"
            >
              {showBalance ? <EyeOff size={22} /> : <Eye size={22} />}
            </button>
          </div>

          <Link
            to="/dashboard/add/money"
            className="bg-white text-[#007AFF] px-4 py-2 md:px-5 md:py-2.5 rounded-lg font-semibold shadow 
                 hover:bg-gray-100 transition inline-block"
          >
            + Add Funds
          </Link>
        </div>
      </section>

      <section className="mt-[70px] bg-[#007AFF] text-white rounded-2xl p-4 flex flex-col gap-4 md:hidden shadow-lg">
        <div className="flex justify-between items-center w-full">
          {/* Left: User Name + Balance */}
          <div className="flex flex-col items-start gap-2">
            <h2 className="text-lg font-bold">Welcome back,</h2>
            <p className="text-base font-medium">
              {profile.firstName} {profile.lastName}
            </p>

            <div className="flex items-center gap-2 mt-1">
              <p className="text-xl font-bold">
                {showBalance
                  ? `₦${(
                      Math.ceil((profile.wallet?.balance || 0) * 100) / 100
                    ).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "••••••"}
              </p>
              <button
                onClick={() => setShowBalance(!showBalance)}
                className="text-white/80 hover:text-white transition"
              >
                {showBalance ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>
          </div>

          {/* Right: Add Funds Button */}
          <Link
            to="/dashboard/add/money"
            className="bg-white text-[#007AFF] px-4 py-2 rounded-lg font-semibold shadow hover:bg-gray-100 transition whitespace-nowrap"
          >
            + Add Funds
          </Link>
        </div>
      </section>

      {/* Main Services */}
      <section className="hidden sm:block bg-[#007AFF]/5 py-12 px-6 rounded-2xl mt-10">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Numbers for SMS Verification
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            Instantly get secure, one-time-use mobile numbers to verify your
            online accounts and apps. Reliable, affordable, and easy to use for
            all your SMS verification needs.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition">
              <div className="w-14 h-14 bg-[#007AFF]/10 flex items-center justify-center rounded-full mb-5 mx-auto">
                <Phone className="w-8 h-8 text-[#007AFF]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                Virtual SMS Numbers
              </h3>
              <p className="text-gray-600">
                Instantly rent SMS-enabled virtual numbers for one-time
                verification on any platform.
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition">
              <div className="w-14 h-14 bg-[#007AFF]/10 flex items-center justify-center rounded-full mb-5 mx-auto">
                <Globe className="w-8 h-8 text-[#007AFF]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                Global Availability
              </h3>
              <p className="text-gray-600">
                Choose SMS verification numbers from multiple countries for your
                favorite services worldwide.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition">
              <div className="w-14 h-14 bg-[#007AFF]/10 flex items-center justify-center rounded-full mb-5 mx-auto">
                <ShieldCheck className="w-8 h-8 text-[#007AFF]" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                Safe & Secure Verifications
              </h3>
              <p className="text-gray-600">
                Protect your privacy by using disposable SMS numbers for OTPs
                and account activations safely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Services */}
      <section className="mt-10">
        <h3 className="text-xl font-bold text-gray-800 mb-6">Quick Services</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              onClick={() => {
                if (service.status === "Available") {
                  window.location.href = service.link; // Or use navigate()
                } else {
                  setModalMessage(
                    "This service is not available yet. Coming soon!"
                  );
                  setShowModal(true);
                }
              }}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition cursor-pointer relative"
            >
              <div className="w-12 h-12 bg-[#007AFF]/10 flex items-center justify-center rounded-lg mb-4">
                {service.icon}
              </div>
              <h4 className="text-lg font-semibold text-gray-700">
                {service.title}
              </h4>
              <p className="text-sm text-gray-500 mt-1">{service.desc}</p>

              <span
                className={`absolute top-4 right-4 text-xs font-medium px-3 py-1 rounded-full ${
                  service.status === "Available"
                    ? "bg-green-100 text-green-600"
                    : "bg-yellow-100 text-yellow-600"
                }`}
              >
                {service.status}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-11/12 max-w-md shadow-lg relative">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Notice</h3>
            <p className="text-gray-600 mb-6">{modalMessage}</p>
            <button
              onClick={() => setShowModal(false)}
              className="px-6 py-2 bg-[#007AFF] text-white rounded-lg font-semibold shadow hover:bg-[#005FCC] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
